/**
 * Own profile — `GET|PATCH auth/me/`, every role. Uses PATCH (partial update)
 * rather than PUT: PUT requires the whole nested `profile` object to be
 * present (users/routes.ts:236-241), and there is no reason to make the
 * caller resend fields it isn't changing.
 *
 * Also hosts the change-password form and, for every role except TENANT, a
 * compact notifications section (unread count + list + mark-as-read/
 * mark-all-as-read). TENANT gets a fuller dedicated page at its own nav
 * destination (`NotificationsPage`) instead — landlord and agent have no
 * separate "Notifications" nav item to hang a full page off of, so their
 * notifications live here.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { changePassword, getCurrentUser, updateProfile } from '../../api/endpoints/auth';
import { listNotifications, markAllAsRead, markAsRead } from '../../api/endpoints/notifications';
import { KENYA_COUNTIES } from '../../types/property';
import {
  disablePushNotifications,
  enablePushNotifications,
  hasRegisteredPushToken,
  isPushConfigured,
} from '../../lib/push';
import type { UpdateProfileRequest } from '../../api/endpoints/auth';

const INITIAL_FORM = {
  first_name: '',
  last_name: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  country: '',
  date_of_birth: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  kra_pin: '',
};

type FormState = typeof INITIAL_FORM;

function toPayload(form: FormState): UpdateProfileRequest {
  return {
    first_name: form.first_name,
    last_name: form.last_name,
    phone: form.phone,
    profile: {
      address: form.address,
      city: form.city,
      state: form.state,
      zip_code: form.zip_code,
      country: form.country,
      date_of_birth: form.date_of_birth === '' ? null : form.date_of_birth,
      emergency_contact_name: form.emergency_contact_name,
      emergency_contact_phone: form.emergency_contact_phone,
    },
    // Node-only, beyond Django parity — see types/user.ts's `User.kra_pin` doc.
    kra_pin: form.kra_pin,
  };
}

const INITIAL_PASSWORD_FORM = { old_password: '', new_password: '', new_password2: '' };

export function ProfilePage(): React.JSX.Element {
  const { user, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const isTenant = user?.role === 'TENANT';

  // --- own profile -----------------------------------------------------
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const profileQuery = useQuery({ queryKey: ['me'], queryFn: getCurrentUser });

  useEffect(() => {
    const data = profileQuery.data;
    if (data === undefined) return;
    setForm({
      first_name: data.first_name,
      last_name: data.last_name,
      phone: data.phone,
      address: data.profile?.address ?? '',
      city: data.profile?.city ?? '',
      state: data.profile?.state ?? '',
      zip_code: data.profile?.zip_code ?? '',
      country: data.profile?.country ?? '',
      date_of_birth: data.profile?.date_of_birth ?? '',
      emergency_contact_name: data.profile?.emergency_contact_name ?? '',
      emergency_contact_phone: data.profile?.emergency_contact_phone ?? '',
      kra_pin: data.kra_pin ?? '',
    });
  }, [profileQuery.data]);

  function update(field: keyof FormState, value: string): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  const save = useMutation({
    mutationFn: () => updateProfile(toPayload(form)),
    onSuccess: (data) => {
      updateUser({
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
        profile: data.profile,
        ...(data.kra_pin !== undefined ? { kra_pin: data.kra_pin } : {}),
      });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (caught) => {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  // --- change password ---------------------------------------------------
  const [passwordForm, setPasswordForm] = useState(INITIAL_PASSWORD_FORM);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const changePasswordMutation = useMutation({
    mutationFn: () => changePassword(passwordForm),
    onSuccess: () => {
      setPasswordForm(INITIAL_PASSWORD_FORM);
      setPasswordError(null);
      setPasswordSuccess(true);
    },
    onError: (caught) => {
      setPasswordSuccess(false);
      // Wrong-password 400 is `{old_password: [...]}` at the TOP level, not
      // the `{errors: {field: [...]}}` shape parseApiError reads — see the
      // route comment on `POST auth/change-password/`.
      if (caught instanceof AxiosError) {
        const body = caught.response?.data as { old_password?: string[] } | undefined;
        if (body?.old_password !== undefined) {
          setPasswordError(body.old_password[0] ?? 'Wrong password.');
          return;
        }
      }
      setPasswordError(parseApiError(caught).message);
    },
  });

  function handlePasswordSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setPasswordSuccess(false);
    // Checked client-side too: the backend enforces it, but a round trip to
    // be told the two boxes differ is a poor form experience.
    if (passwordForm.new_password !== passwordForm.new_password2) {
      setPasswordError("Password fields didn't match.");
      return;
    }
    setPasswordError(null);
    changePasswordMutation.mutate();
  }

  // --- push notifications (FCM web) ---------------------------------------
  // Hidden entirely when isPushConfigured() is false (no Firebase project
  // provisioned yet — see PARITY_NOTES.md) rather than shown as a button
  // that would error when clicked.
  const [pushRegistered, setPushRegistered] = useState(hasRegisteredPushToken());
  const [pushError, setPushError] = useState<string | null>(null);

  const enablePush = useMutation({
    mutationFn: enablePushNotifications,
    onSuccess: (result) => {
      if (result.ok) {
        setPushRegistered(true);
        setPushError(null);
      } else if (result.reason === 'permission-denied') {
        setPushError('Notification permission was denied in the browser.');
      } else {
        setPushError('Could not enable push notifications.');
      }
    },
  });

  const disablePush = useMutation({
    mutationFn: disablePushNotifications,
    onSuccess: () => setPushRegistered(false),
  });

  // --- notifications (compact, every role except TENANT) -----------------
  const notificationsQuery = useQuery({
    queryKey: ['notifications', { page: 1 }],
    queryFn: () => listNotifications({ page: 1 }),
    enabled: !isTenant,
  });

  const markRead = useMutation({
    mutationFn: (id: number) => markAsRead(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllRead = useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const notifications = notificationsQuery.data?.results ?? [];
  const unreadCount = notifications.filter((notification) => !notification.is_read).length;

  if (profileQuery.isPending) return <p>Loading…</p>;
  if (profileQuery.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(profileQuery.error).message}
      </p>
    );
  }

  return (
    <section>
      <h2>Profile</h2>

      <form onSubmit={handleSubmit} noValidate>
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <label htmlFor="profile-first-name">First name</label>
        <input
          id="profile-first-name"
          required
          value={form.first_name}
          onChange={(event) => update('first_name', event.target.value)}
        />
        {fieldError('first_name') !== undefined && (
          <p className="form__field-error">{fieldError('first_name')}</p>
        )}

        <label htmlFor="profile-last-name">Last name</label>
        <input
          id="profile-last-name"
          required
          value={form.last_name}
          onChange={(event) => update('last_name', event.target.value)}
        />
        {fieldError('last_name') !== undefined && (
          <p className="form__field-error">{fieldError('last_name')}</p>
        )}

        <label htmlFor="profile-phone">Phone</label>
        <input
          id="profile-phone"
          type="tel"
          value={form.phone}
          onChange={(event) => update('phone', event.target.value)}
        />

        <label htmlFor="profile-address">Address</label>
        <input
          id="profile-address"
          value={form.address}
          onChange={(event) => update('address', event.target.value)}
        />

        <label htmlFor="profile-city">City</label>
        <input
          id="profile-city"
          value={form.city}
          onChange={(event) => update('city', event.target.value)}
        />

        <label htmlFor="profile-state">County</label>
        <select
          id="profile-state"
          value={form.state}
          onChange={(event) => update('state', event.target.value)}
        >
          <option value="">— Select a county —</option>
          {KENYA_COUNTIES.map((county) => (
            <option key={county} value={county}>
              {county}
            </option>
          ))}
        </select>

        <label htmlFor="profile-zip">Postal code (optional)</label>
        <input
          id="profile-zip"
          value={form.zip_code}
          onChange={(event) => update('zip_code', event.target.value)}
        />

        <label htmlFor="profile-country">Country</label>
        <input
          id="profile-country"
          value={form.country}
          onChange={(event) => update('country', event.target.value)}
        />

        <label htmlFor="profile-dob">Date of birth</label>
        <input
          id="profile-dob"
          type="date"
          value={form.date_of_birth}
          onChange={(event) => update('date_of_birth', event.target.value)}
        />

        <label htmlFor="profile-emergency-name">Emergency contact name</label>
        <input
          id="profile-emergency-name"
          value={form.emergency_contact_name}
          onChange={(event) => update('emergency_contact_name', event.target.value)}
        />

        <label htmlFor="profile-emergency-phone">Emergency contact phone</label>
        <input
          id="profile-emergency-phone"
          type="tel"
          value={form.emergency_contact_phone}
          onChange={(event) => update('emergency_contact_phone', event.target.value)}
        />

        {!isTenant && (
          <>
            <label htmlFor="profile-kra-pin">KRA PIN (optional)</label>
            <input
              id="profile-kra-pin"
              placeholder="A012345678Z"
              value={form.kra_pin}
              onChange={(event) => update('kra_pin', event.target.value)}
            />
            <p className="form__hint">Used on the tax summary report — see Reports.</p>
            {fieldError('kra_pin') !== undefined && (
              <p className="form__field-error">{fieldError('kra_pin')}</p>
            )}
          </>
        )}

        <button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        {save.isSuccess && <span role="status"> Profile updated.</span>}
      </form>

      <h3>Change password</h3>
      <form onSubmit={handlePasswordSubmit} noValidate>
        {passwordError !== null && (
          <p role="alert" className="form__error">
            {passwordError}
          </p>
        )}
        {passwordSuccess && <p role="status">Password changed successfully.</p>}

        <label htmlFor="password-old">Current password</label>
        <input
          id="password-old"
          type="password"
          autoComplete="current-password"
          required
          value={passwordForm.old_password}
          onChange={(event) =>
            setPasswordForm((previous) => ({ ...previous, old_password: event.target.value }))
          }
        />

        {/* Backend rule: at least 8 characters and not entirely numeric
            (refactor/backend/src/lib/passwordValidation.ts). */}
        <label htmlFor="password-new">New password</label>
        <input
          id="password-new"
          type="password"
          autoComplete="new-password"
          required
          value={passwordForm.new_password}
          onChange={(event) =>
            setPasswordForm((previous) => ({ ...previous, new_password: event.target.value }))
          }
        />

        <label htmlFor="password-new2">Confirm new password</label>
        <input
          id="password-new2"
          type="password"
          autoComplete="new-password"
          required
          value={passwordForm.new_password2}
          onChange={(event) =>
            setPasswordForm((previous) => ({ ...previous, new_password2: event.target.value }))
          }
        />

        <button type="submit" disabled={changePasswordMutation.isPending}>
          {changePasswordMutation.isPending ? 'Changing…' : 'Change password'}
        </button>
      </form>

      {isPushConfigured() && (
        <section aria-label="Push notifications">
          <h3>Push notifications</h3>
          {pushError !== null && (
            <p role="alert" className="form__error">
              {pushError}
            </p>
          )}
          {pushRegistered ? (
            <button type="button" disabled={disablePush.isPending} onClick={() => disablePush.mutate()}>
              {disablePush.isPending ? 'Disabling…' : 'Disable push notifications on this device'}
            </button>
          ) : (
            <button type="button" disabled={enablePush.isPending} onClick={() => enablePush.mutate()}>
              {enablePush.isPending ? 'Enabling…' : 'Enable push notifications on this device'}
            </button>
          )}
        </section>
      )}

      {!isTenant && (
        <section aria-label="Notifications">
          <h3>Notifications{unreadCount > 0 && ` (${unreadCount} unread)`}</h3>

          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending || unreadCount === 0}
          >
            Mark all as read
          </button>

          {notificationsQuery.isPending && <p>Loading…</p>}
          {notificationsQuery.isError && (
            <p role="alert" className="form__error">
              {parseApiError(notificationsQuery.error).message}
            </p>
          )}
          {notificationsQuery.isSuccess && notifications.length === 0 && <p>No notifications.</p>}

          {notifications.length > 0 && (
            <ul>
              {notifications.map((notification) => (
                <li key={notification.id}>
                  <strong>{notification.title}</strong> — {notification.message}{' '}
                  {notification.is_read ? (
                    <em>Read</em>
                  ) : (
                    <button
                      type="button"
                      onClick={() => markRead.mutate(notification.id)}
                      disabled={markRead.isPending}
                    >
                      Mark as read
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </section>
  );
}

export default ProfilePage;
